import { useEffect, useMemo, useRef, useState } from 'react';
import { CopyButton } from '@smworks-cz/ui-kit';
import { verifiedTranscripts, type MessageDTO } from '@sam/shared';
import { Markdown } from './Markdown';
import { AttachmentTile } from './Attachment';
import { ChatWidget } from './widgets/WidgetCard';
import { splitWidgets } from '../lib/splitWidgets';
import { DeniedNotice, LiveSteps, StepsSummary } from './Steps';
import { animatedMessages, useLiveTurn } from '../lib/turns';
import { Typewriter } from '../lib/typewriter';
import { fmtTime } from '../lib/format';

export function UserMessage({ m }: { m: MessageDTO }) {
  return (
    <div className="mu-wrap">
      {!!m.attachments?.length && (
        <div className="mu-files">
          {m.attachments.map((a) => (
            <AttachmentTile key={a.id} name={a.name} mime={a.mime} size={a.size} src={`/api/files/${a.id}`} big />
          ))}
        </div>
      )}
      {m.content && <div className="mu">{m.content}</div>}
    </div>
  );
}

/** Text vypisovaný po slovech. Zprávy z historie se ukážou celé. */
function useTypedText(text: string, animate: boolean): { visible: string; typing: boolean } {
  const tw = useRef<Typewriter | null>(null);
  if (!tw.current) tw.current = new Typewriter(text, !animate);
  const [, force] = useState(0);
  useEffect(() => {
    const w = tw.current!;
    w.set(text);
    if (w.done()) {
      force((n) => n + 1);
      return;
    }
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (w.tick(dt)) force((n) => n + 1);
      if (!w.done()) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [text]);
  return { visible: tw.current.visible(), typing: !tw.current.done() };
}

function Dots({ label }: { label: string }) {
  return (
    <p className="status-line">
      <span className="dots" aria-hidden>
        <span />
        <span />
        <span />
      </span>
      {label}
    </p>
  );
}

export function AssistantMessage({ m }: { m: MessageDTO }) {
  const lt = useLiveTurn(m.id);
  const isLive = !!lt;
  const text = isLive ? lt.text : m.content;
  const { visible, typing } = useTypedText(text, animatedMessages.has(m.id));
  // Vypisování jen jednou, když zpráva přijde: po dopsání (nebo když uživatel odejde jinam) se zpráva
  // při dalším otevření chatu ukáže rovnou celá.
  const liveRef = useRef(isLive);
  liveRef.current = isLive;
  useEffect(() => {
    if (!isLive && !typing) animatedMessages.delete(m.id);
  }, [isLive, typing, m.id]);
  useEffect(
    () => () => {
      if (!liveRef.current) animatedMessages.delete(m.id);
    },
    [m.id],
  );
  const steps = isLive ? lt.steps : (m.steps ?? []);
  // Během psaní se ještě neví, co agent načetl: odkazy do přepisu se zpřístupní po dokončení tahu.
  const verified = useMemo(() => (isLive ? undefined : verifiedTranscripts(m.sources)), [isLive, m.sources]);
  const phase = lt?.phase;

  return (
    <div className="ma">
      <span className="avatar-s" aria-hidden>
        S
      </span>
      <div className="ma-body">
        {isLive ? <LiveSteps steps={steps} /> : <StepsSummary steps={steps} />}
        {!isLive && <DeniedNotice steps={steps} />}
        {isLive && !text && (phase === 'thinking' || (phase === 'tools' && steps.every((s) => s.status !== 'running'))) && <Dots label="Přemýšlím…" />}
        {isLive && !text && phase === 'writing' && <Dots label="Píšu odpověď…" />}
        {visible &&
          splitWidgets(visible).map((seg, i) =>
            seg.kind === 'md' ? (
              <Markdown key={i} text={seg.text} verified={verified} />
            ) : (
              <ChatWidget
                key={i}
                json={seg.json}
                closed={seg.closed}
                index={seg.index}
                live={isLive || typing}
                messageId={m.id}
                datasets={m.datasets}
              />
            ),
          )}
        {!isLive && m.status === 'error' && (
          <div className="msg-error" role="alert">
            {m.error || 'Odpověď se nepodařilo dokončit.'}
          </div>
        )}
        {!isLive && !typing && m.status === 'done' && (
          <div className="foot">
            {m.sources && m.sources.length > 0 && (
              <span>
                Zdroj: {m.sources.map((s) => s.label).join(', ')} · načteno {fmtTime(m.sources[0].fetchedAt)}
              </span>
            )}
            <CopyButton text={m.content} size="sm" label="Kopírovat odpověď" />
          </div>
        )}
      </div>
    </div>
  );
}
